/**
 * Gallery — grouping photos by room (#1694). Pure: no React, no DOM.
 *
 * Port of the toolkit's PropertyGallery\Rooms (includes/PropertyGallery/
 * Rooms.php, toolkit#767 / #773) and the planners in assets/js/
 * property-gallery.js, so a site that moves from the WordPress gallery to the
 * platform one gets the same sections in the same order with the same
 * headings. The toolkit's unit tests re-assert against this file
 * (test/widgets/gallery-rooms.test.ts).
 *
 * Operators assign listing photos to rooms in the PMS (Guesty spaces) and
 * Central denormalizes the room onto each photo (central#3626): `room_uid` is
 * the grouping key, `room_name` the heading as the operator typed it,
 * `room_type` a token such as BEDROOM or FULL_BATHROOM. Names repeat within a
 * listing ("Full bathroom" x3), so photos group by uid and never by name.
 *
 * Rooms are ordered by where each room's first photo sits in the gallery,
 * which is the order the operator curated (Lakefront's gallery opens on the
 * dock, the backyard and the deck; the PMS room number would put the bedrooms
 * first). A site can put named rooms, or whole room types, ahead of that
 * order. Photos with no room close the list under "More photos". A listing
 * with no room data at all groups to null and the caller renders the plain
 * gallery — which is every listing until the public API serializes the room
 * fields (central#3703).
 *
 * A site can also rename a room: a rule "match = display" renames the room
 * whose uid, or whose heading, is `match` (a heading rule takes every room
 * with that heading), and rooms that end up with the same display merge into
 * one group. That is the one place photos group across uids, and only because
 * the operator asked for it by name.
 */

/** The room fields a photo row carries once central#3703 ships; all optional. */
export type RoomPhoto = {
  room_uid?: string | null
  room_name?: string | null
  room_type?: string | null
  room_position?: number | null
}

export type RoomGroup<T extends RoomPhoto = RoomPhoto> = {
  /** The room uid, `override:<display>` for a renamed or merged room, or MORE_PHOTOS_KEY. */
  key: string
  /** The heading guests see. */
  name: string
  /** The first room type seen for the group, null when none. */
  type: string | null
  /** The group's photos in gallery order; the same objects that came in. */
  photos: T[]
}

export type RoomOverride = { match: string; display: string }

/** Group key of the closing "More photos" group (photos with no room). */
export const MORE_PHOTOS_KEY = ''
/** Most entries a custom room order keeps; the rest of the text is dropped. */
export const MAX_ROOM_ORDER_ENTRIES = 100
/** Most override rules kept; the rest of the text is dropped. */
export const MAX_ROOM_OVERRIDE_RULES = 100
/** Group key prefix of a room renamed (or merged) by an override rule. */
export const OVERRIDE_KEY_PREFIX = 'override:'

const DEFAULT_LABELS = { more: 'More photos', room: 'Room' }

/** A scalar as trimmed text; anything else is ''. (PHP's is_scalar: string, number, boolean.) */
const text = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value).trim() : ''

/**
 * Normalize a room name, room type or order entry for comparison: lower-cased,
 * every run of characters that are not letters or digits collapsed to one
 * space, trimmed. "Dock & Boathouse", "dock boathouse" and "DOCK_BOATHOUSE"
 * all agree. '' when nothing comparable remains.
 */
export function normalizeRoomText(value: string): string {
  return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
}

/**
 * Heading for a room: its name; else its type in Title Case with the
 * underscores as spaces ("FULL_BATHROOM" is "Full Bathroom"); else "Room".
 */
export function roomHeading(name: string, type: string, roomLabel: string = DEFAULT_LABELS.room): string {
  if (name !== '') return name
  if (type !== '') {
    const words = type.replace(/[\s_]+/g, ' ').trim()
    if (words !== '') {
      return words.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_m, space: string, letter: string) => space + letter.toUpperCase())
    }
  }
  return roomLabel
}

/**
 * Parse the room-order text: one entry per line, trimmed, blank lines
 * dropped, at most MAX_ROOM_ORDER_ENTRIES entries.
 */
export function parseRoomOrder(raw: string): string[] {
  const entries: string[] = []
  for (const line of raw.split(/\r\n|\r|\n/)) {
    const entry = line.trim()
    if (entry === '') continue
    entries.push(entry)
    if (entries.length >= MAX_ROOM_ORDER_ENTRIES) break
  }
  return entries
}

/**
 * Parse the room-overrides text: one rule per line as "match = display",
 * split on the first "=", both sides trimmed. A line with no "=", an empty
 * match, or a display with no letter or digit is dropped; at most
 * MAX_ROOM_OVERRIDE_RULES rules are kept.
 */
export function parseRoomOverrides(raw: string): RoomOverride[] {
  const rules: RoomOverride[] = []
  for (const line of raw.split(/\r\n|\r|\n/)) {
    const eq = line.indexOf('=')
    if (eq < 0) continue
    const match = line.slice(0, eq).trim()
    const display = line.slice(eq + 1).trim()
    if (match === '' || normalizeRoomText(display) === '') continue
    rules.push({ match, display })
    if (rules.length >= MAX_ROOM_OVERRIDE_RULES) break
  }
  return rules
}

type InternalRoom<T> = {
  key: string
  name: string
  type: string
  types: Set<string>
  photos: T[]
  normName: string
  normTypes: string[]
}

/**
 * Which rooms the override rules rename: uid -> { key, display }.
 *
 * A rule's match is compared to each room's uid, exactly and then through
 * normalizeRoomText(), and to the room's heading (its name, else its type the
 * way the heading shows it) through normalizeRoomText(): "Upper Level - hall
 * bath" and "upper level hall bath" both find the room, and so does its id in
 * any casing. A uid match beats a heading match; among rules of one kind the
 * first wins. A heading rule takes every room with that heading. Rooms that
 * end up with the same display (after normalizeRoomText(); the first spelling
 * met shows) share one key, and an unrenamed room whose own heading already is
 * that display joins them, so renaming a room to an existing heading merges
 * the two. A rule missing a side, or whose display normalizes to nothing, is
 * ignored.
 */
function renamedRooms<T extends RoomPhoto>(photos: T[], overrides: RoomOverride[], roomLabel: string): Map<string, { key: string; display: string }> {
  const byUid = new Map<string, string>()
  const byNorm = new Map<string, string>()
  for (const rule of overrides) {
    if (!rule || typeof rule !== 'object') continue
    const match = text((rule as RoomOverride).match)
    const display = text((rule as RoomOverride).display)
    if (match === '' || display === '' || normalizeRoomText(display) === '') continue
    if (!byUid.has(match)) byUid.set(match, display)
    const needle = normalizeRoomText(match)
    if (needle !== '' && !byNorm.has(needle)) byNorm.set(needle, display)
  }
  const renamed = new Map<string, { key: string; display: string }>()
  if (byUid.size === 0) return renamed

  // Each room's name and type, resolved the way groupPhotosByRoom resolves them.
  const names = new Map<string, string>()
  const types = new Map<string, string>()
  for (const photo of photos) {
    if (!photo || typeof photo !== 'object') continue
    const uid = text(photo.room_uid)
    if (uid === '') continue
    if ((names.get(uid) ?? '') === '') names.set(uid, text(photo.room_name))
    if ((types.get(uid) ?? '') === '') types.set(uid, text(photo.room_type))
  }

  const headings = new Map<string, string>()
  for (const [uid, name] of names) {
    headings.set(uid, normalizeRoomText(roomHeading(name, types.get(uid) ?? '', roomLabel)))
  }

  const targets = new Map<string, string>()
  for (const [uid, heading] of headings) {
    const display = byUid.get(uid) ?? byNorm.get(normalizeRoomText(uid)) ?? byNorm.get(heading)
    if (display === undefined) continue
    const norm = normalizeRoomText(display)
    if (!targets.has(norm)) targets.set(norm, display)
    renamed.set(uid, { key: OVERRIDE_KEY_PREFIX + norm, display: targets.get(norm)! })
  }

  // A room that already carries a display's heading joins that group.
  for (const [uid, heading] of headings) {
    if (!renamed.has(uid) && targets.has(heading)) {
      renamed.set(uid, { key: OVERRIDE_KEY_PREFIX + heading, display: targets.get(heading)! })
    }
  }
  return renamed
}

const exportGroup = <T extends RoomPhoto>(room: InternalRoom<T>): RoomGroup<T> => ({
  key: room.key,
  name: room.name,
  type: room.type === '' ? null : room.type,
  photos: room.photos,
})

/**
 * Group photos by room, in display order.
 *
 * @param photos      Photos in gallery order, each possibly carrying room_uid /
 *                    room_name / room_type (null or absent = no room). Anything
 *                    else on a photo flows through untouched.
 * @param customOrder Ordered room names or room types (parseRoomOrder()).
 * @param overrides   Rename rules (parseRoomOverrides()).
 * @param labels      The "More photos" and "Room" headings, for translation.
 * @returns Groups in display order, or null when no photo carries a room.
 */
export function groupPhotosByRoom<T extends RoomPhoto>(
  photos: T[],
  customOrder: string[] = [],
  overrides: RoomOverride[] = [],
  labels: { more?: string; room?: string } = {},
): RoomGroup<T>[] | null {
  const moreLabel = labels.more ?? DEFAULT_LABELS.more
  const roomLabel = labels.room ?? DEFAULT_LABELS.room
  const rooms = new Map<string, InternalRoom<T>>() // insertion order = first photo order
  const more: T[] = []
  const renamed = renamedRooms(photos, overrides, roomLabel)

  for (const photo of photos) {
    if (!photo || typeof photo !== 'object') continue
    const uid = text(photo.room_uid)
    if (uid === '') {
      more.push(photo)
      continue
    }
    // A renamed room groups under its display; rooms given the same display
    // share the key and so merge, their photos in gallery order.
    const key = renamed.get(uid)?.key ?? uid
    let room = rooms.get(key)
    if (!room) {
      room = { key, name: renamed.get(uid)?.display ?? '', type: '', types: new Set(), photos: [], normName: '', normTypes: [] }
      rooms.set(key, room)
    }
    // Every photo of a room carries the same name and type; the first
    // non-empty value wins in case a row lost one of them. (A renamed room
    // already holds its display, so its PMS name never lands.)
    if (room.name === '') room.name = text(photo.room_name)
    const type = text(photo.room_type)
    if (type !== '') {
      if (room.type === '') room.type = type
      room.types.add(type)
    }
    room.photos.push(photo)
  }

  if (rooms.size === 0) return null

  for (const room of rooms.values()) {
    room.name = roomHeading(room.name, room.type, roomLabel)
    room.normName = normalizeRoomText(room.name)
    room.normTypes = [...room.types].map(normalizeRoomText)
  }

  const groups: RoomGroup<T>[] = []
  const placed = new Set<string>()

  // Each entry takes the rooms it names, then the rooms of that type (so
  // "BEDROOM" pulls every bedroom); several rooms in one slot keep their
  // default relative order, and a room is placed once.
  for (const entry of customOrder) {
    const needle = typeof entry === 'string' || typeof entry === 'number' ? normalizeRoomText(String(entry)) : ''
    if (needle === '') continue
    for (const kind of ['name', 'type'] as const) {
      for (const [key, room] of rooms) {
        const hit = kind === 'name' ? room.normName === needle : room.normTypes.includes(needle)
        if (placed.has(key) || !hit) continue
        placed.add(key)
        groups.push(exportGroup(room))
      }
    }
  }

  for (const [key, room] of rooms) {
    if (!placed.has(key)) groups.push(exportGroup(room))
  }

  if (more.length > 0) {
    groups.push({ key: MORE_PHOTOS_KEY, name: moreLabel, type: null, photos: more })
  }

  return groups
}

// ---------------------------------------------------------------------------
// Photo tour planners (assets/js/property-gallery.js)
// ---------------------------------------------------------------------------

export type MosaicBlock = { kind: 'full' | 'pair' | 'trio'; flip: boolean; idx: number[] }

/**
 * Photo tour mosaic for one room: `count` photos starting at `start`, as
 * blocks of one photo (full width), two (side by side) or three (two stacked
 * beside one tall; `flip` puts the tall photo on the left, and trios alternate
 * so a long room does not lean one way). A room never ends on a lone
 * full-width photo unless it has exactly one: a trailing four becomes two
 * pairs.
 */
export function planMosaic(start: number, count: number): MosaicBlock[] {
  const blocks: MosaicBlock[] = []
  let flip = false
  let i = 0
  while (i < count) {
    const left = count - i
    const size = left === 1 ? 1 : left === 2 || left === 4 ? 2 : 3
    const idx: number[] = []
    for (let k = 0; k < size; k++) idx.push(start + i + k)
    if (size === 3) {
      blocks.push({ kind: 'trio', flip, idx })
      flip = !flip
    } else {
      blocks.push({ kind: size === 1 ? 'full' : 'pair', flip: false, idx })
    }
    i += size
  }
  return blocks
}

export type TourSection<T extends RoomPhoto = RoomPhoto> = {
  key: string
  title: string
  /** Index of the section's first photo in the tour order. */
  start: number
  count: number
  /** The closing "More photos" group (gets a collage tile instead of a cover photo). */
  rest: boolean
  photos: T[]
  blocks: MosaicBlock[]
}

/**
 * The groups as tour sections over ONE index space: photo indexes run through
 * the sections in order, so the lightbox's prev/next walk every room as one
 * sequence and its counter reads n / total.
 */
export function buildTourSections<T extends RoomPhoto>(groups: RoomGroup<T>[]): TourSection<T>[] {
  let offset = 0
  return groups.map((group) => {
    const section: TourSection<T> = {
      key: group.key,
      title: group.name,
      start: offset,
      count: group.photos.length,
      rest: group.key === MORE_PHOTOS_KEY,
      photos: group.photos,
      blocks: planMosaic(offset, group.photos.length),
    }
    offset += group.photos.length
    return section
  })
}

/**
 * Where a gallery-order photo sits in the tour order (identity lookup: the
 * tour holds the same objects). Falls back to the gallery index when the two
 * orders are the same array or the photo is not found.
 */
export function galleryIndexToTourIndex<T>(images: T[], ordered: T[], galleryIndex: number): number {
  if (images === ordered) return galleryIndex
  const photo = images[galleryIndex]
  const tourIndex = photo === undefined ? -1 : ordered.indexOf(photo)
  return tourIndex < 0 ? galleryIndex : tourIndex
}

// ---------------------------------------------------------------------------
// Pinned room index (toolkit#806)
// ---------------------------------------------------------------------------

/**
 * The compact bar of room names shows once the full index has scrolled up
 * under the browser's header, and goes away when the guest is back at the top.
 */
export function tourBarShouldShow(indexBottom: number, headerBottom: number): boolean {
  return indexBottom <= headerBottom
}

/**
 * The room in view: the last section whose top is at or above the reading
 * line (just under the header and the bar); the first one until any section
 * has reached it.
 */
export function activeSectionFor(tops: number[], line: number): number {
  let active = 0
  for (let i = 0; i < tops.length; i++) {
    if (tops[i] <= line) active = i
  }
  return active
}
